import { sanitizeAiHtml } from './sanitizeHtml';

export type CounselPanelSection = {
  kind: 'brief' | 'draft' | 'body';
  html: string;
};

/** Best-effort severity marker styling — never throws; leaves text unchanged if no match. */
export function applyCounselSeverityMarkers(html: string): string {
  if (!html || typeof html !== 'string') return html;
  try {
    return html
      .replace(
        /\[Critical\]/gi,
        '<span class="counsel-severity-critical">[Critical]</span>',
      )
      .replace(
        /\[Warning\]/gi,
        '<span class="counsel-severity-medium">[Warning]</span>',
      )
      .replace(
        /\[Medium\]/gi,
        '<span class="counsel-severity-medium">[Medium]</span>',
      )
      .replace(
        /\[Suggestion\]/gi,
        '<span class="counsel-severity-suggestion">[Suggestion]</span>',
      );
  } catch {
    return html;
  }
}

/**
 * Strip document-layout indents from HTML shown in the narrow side pane.
 * Draft HTML often includes signature / caption margins (e.g. 288–378px) meant for A4 pages.
 */
export function stripCounselPanelLayoutIndents(html: string): string {
  if (!html || typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') {
    return html
      .replace(
        /(?:margin-left|margin-right|padding-left|padding-right|text-indent)\s*:\s*[^;}"']+;?/gi,
        '',
      )
      .replace(/\sstyle=(["'])\s*\1/gi, '');
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('[style]').forEach((el) => {
    const style = (el as HTMLElement).style;
    style.removeProperty('margin-left');
    style.removeProperty('margin-right');
    style.removeProperty('padding-left');
    style.removeProperty('padding-right');
    style.removeProperty('text-indent');

    // Collapse left/right components of margin/padding shorthands.
    const margin = style.margin;
    if (margin) {
      const parts = margin.trim().split(/\s+/);
      if (parts.length === 4) {
        style.margin = `${parts[0]} 0 ${parts[2]} 0`;
      } else if (parts.length === 3) {
        style.margin = `${parts[0]} 0 ${parts[2]}`;
      } else if (parts.length === 2) {
        style.margin = `${parts[0]} 0`;
      }
    }
    const padding = style.padding;
    if (padding) {
      const parts = padding.trim().split(/\s+/);
      if (parts.length === 4) {
        style.padding = `${parts[0]} 0 ${parts[2]} 0`;
      } else if (parts.length === 3) {
        style.padding = `${parts[0]} 0 ${parts[2]}`;
      } else if (parts.length === 2) {
        style.padding = `${parts[0]} 0`;
      }
    }

    if (!(el as HTMLElement).getAttribute('style')?.trim()) {
      el.removeAttribute('style');
    }
  });

  return doc.body.innerHTML;
}

/** Normalize counsel chat HTML for the side panel (markdown → HTML, sanitize). */
export function formatCounselPanelHtml(raw: string): string {
  let html = raw.trim();
  if (!html) return '';

  if (!/<[a-z][\s\S]*>/i.test(html)) {
    html = markdownishToHtml(html);
  } else {
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/^#{1,3}\s+(.+)$/gm, (_, title) => `<h3>${title.trim()}</h3>`);
  }

  html = applyCounselSeverityMarkers(html);

  if (!html.includes('data-lexgat-counsel=')) {
    html = `<section data-lexgat-counsel="brief">${html}</section>`;
  }

  return stripCounselPanelLayoutIndents(sanitizeAiHtml(html));
}

export function markdownishToHtml(text: string): string {
  const lines = text.split('\n');
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let listOrdered = false;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const body = paragraph.join(' ').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    blocks.push(`<p>${body}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const tag = listOrdered ? 'ol' : 'ul';
    blocks.push(
      `<${tag}>${listItems.map((item) => `<li>${item}</li>`).join('')}</${tag}>`,
    );
    listItems = [];
    listOrdered = false;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    const h3 = trimmed.match(/^#{1,3}\s+(.+)$/);
    if (h3) {
      flushParagraph();
      flushList();
      blocks.push(`<h3>${h3[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</h3>`);
      continue;
    }

    const h4 = trimmed.match(/^\d+\.\s+\*\*(.+?)\*\*:?\s*$/);
    if (h4) {
      flushParagraph();
      flushList();
      blocks.push(`<h4>${h4[1]}</h4>`);
      continue;
    }

    const h4Plain = trimmed.match(/^\d+\.\s+(.+?):?\s*$/);
    if (h4Plain && h4Plain[1].length < 80) {
      flushParagraph();
      flushList();
      blocks.push(`<h4>${h4Plain[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</h4>`);
      continue;
    }

    const ul = trimmed.match(/^[-*•]\s+(.+)$/);
    if (ul) {
      flushParagraph();
      if (listOrdered && listItems.length) flushList();
      listOrdered = false;
      listItems.push(ul[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'));
      continue;
    }

    const ol = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ol) {
      flushParagraph();
      if (!listOrdered && listItems.length) flushList();
      listOrdered = true;
      listItems.push(ol[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'));
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }

  flushParagraph();
  flushList();
  return blocks.join('');
}

/** Markdown → HTML for editor drafts (numbered lines stay as lists, not headings). */
export function counselDraftMarkdownToHtml(text: string): string {
  const lines = text.split('\n');
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let listOrdered = false;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const body = paragraph.join(' ').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    blocks.push(`<p>${body}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const tag = listOrdered ? 'ol' : 'ul';
    blocks.push(
      `<${tag}>${listItems.map((item) => `<li><p>${item}</p></li>`).join('')}</${tag}>`,
    );
    listItems = [];
    listOrdered = false;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    const h3 = trimmed.match(/^#{1,3}\s+(.+)$/);
    if (h3) {
      flushParagraph();
      flushList();
      blocks.push(`<h3>${h3[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</h3>`);
      continue;
    }

    const h4 = trimmed.match(/^\d+\.\s+\*\*(.+?)\*\*:?\s*$/);
    if (h4) {
      flushParagraph();
      flushList();
      blocks.push(`<h4>${h4[1]}</h4>`);
      continue;
    }

    const ul = trimmed.match(/^[-*•]\s+(.+)$/);
    if (ul) {
      flushParagraph();
      if (listOrdered && listItems.length) flushList();
      listOrdered = false;
      listItems.push(ul[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'));
      continue;
    }

    const ol = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ol) {
      flushParagraph();
      if (!listOrdered && listItems.length) flushList();
      listOrdered = true;
      listItems.push(ol[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'));
      continue;
    }

    const alpha = trimmed.match(/^\(([a-z]+)\)\s+(.+)$/i);
    if (alpha) {
      flushParagraph();
      if (!listOrdered && listItems.length) flushList();
      listOrdered = true;
      listItems.push(alpha[2].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'));
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }

  flushParagraph();
  flushList();
  return blocks.join('');
}

/** Split counsel HTML into brief / draft sections for structured panel layout. */
export function parseCounselPanelSections(html: string): CounselPanelSection[] {
  const formatted = formatCounselPanelHtml(html);
  if (typeof DOMParser === 'undefined') {
    return [{ kind: 'body', html: formatted }];
  }

  const doc = new DOMParser().parseFromString(formatted, 'text/html');
  const sections = Array.from(doc.querySelectorAll('section[data-lexgat-counsel]'));

  if (sections.length === 0) {
    const body = doc.body.innerHTML.trim();
    return body ? [{ kind: 'body', html: body }] : [];
  }

  return sections.map((section) => {
    const kind = section.getAttribute('data-lexgat-counsel');
    return {
      kind: kind === 'draft' ? 'draft' : 'brief',
      html: section.innerHTML.trim(),
    };
  });
}

export function isCounselDraftPlaceholder(html: string): boolean {
  const plain = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return (
    plain.includes('no formal draft text requested') ||
    plain.includes('guidance is in my response above')
  );
}
